"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const common_1 = require("../common");
const CreaseEdge_1 = require("./CreaseEdge");
/**
 * A reference to an edge in a Crease object.
 */
class CreaseEdgeRef {
    /**
     * Constructs a CreaseEdgeRef object.
     * @param {CreaseEdge} edge The edge to which this instance refers.
     * @param {boolean} isReversed True if the edge is traversed in the reverse direction.
     */
    constructor(edge, isReversed = false) {
        this.startAngle = 0; // used internally
        this.endAngle = 0; // used internally
        // Check inputs.
        common_1.check.isInstanceOf('edge', edge, CreaseEdge_1.CreaseEdge);
        common_1.check.isBoolean('isReversed', isReversed);
        this._edge = edge;
        this._isReversed = isReversed;
    }
    /**
     * Returns the edge to which this instance refers.
     * @returns {CreaseEdge} The edge to which this instance refers.
     */
    get edge() {
        return this._edge;
    }
    /**
     * Returns true if the edge is traversed in the reverse direction.
     * @returns {boolean} True if the edge is traversed in the reverse direction.
     */
    get isReversed() {
        return this._isReversed;
    }
    /**
     * Returns the first vertex traversed by this edge reference.
     * @returns {CreaseVertex} The first vertex traversed by this edge reference.
     */
    get vertex1() {
        return this.isReversed ? this.edge.vertex2 : this.edge.vertex1;
    }
    /**
     * Returns the second vertex traversed by this edge reference.
     * @returns {CreaseVertex} The second vertex traversed by this edge reference.
     */
    get vertex2() {
        return this.isReversed ? this.edge.vertex1 : this.edge.vertex2;
    }
    /**
     * Returns the face to the left of this edge reference (or undefined, for a reversed reference
     * to a boundary edge).
     * @returns {CreaseFace | undefined} The face to the left of this edge reference.
     */
    get leftFace() {
        return this.isReversed ? this.edge.rightFace : this.edge.leftFace;
    }
    /**
     * Returns the face to the right of this edge reference (or undefined, for an unreversed
     * reference to a boundary edge).
     * @returns {CreaseFace | undefined} The face to the right of this edge reference.
     */
    get rightFace() {
        return this.isReversed ? this.edge.leftFace : this.edge.rightFace;
    }
    /**
     * Returns true if this is a boundary edge, meaning it has only one adjacent face.
     * The face may be to the left (if we're traversing an edge in the forward direction), or to
     * the right (if we're traversing an edge in the reverse direction).
     * @returns {boolean} True if this is a boundary edge reference; false otherwise.
     */
    get isBoundary() {
        return this.edge.isBoundary;
    }
    /**
     * Returns the length of the edge.
     * @returns {number} Edge length.
     */
    get length() {
        return this.edge.length;
    }
    /**
     * Returns the assignment of this edge.
     * @returns {CreaseEdgeAssignment} The assignment of this edge.
     */
    get assignment() {
        return this.edge.assignment;
    }
    /**
     * Returns true if this is a crease edge , meaning its assignment is 'U', 'M', or 'V'.
     * @returns {boolean} True if this is a crease edge; false otherwise.
     */
    get isCrease() {
        return this.edge.isCrease;
    }
    /**
     * Returns an array of 2D curve control points for this edge.
     * @returns {Vector2[]} An array of 2D curve control points for this edge.
     */
    get controlPoints() {
        let { controlPoints } = this.edge;
        if (this.isReversed) {
            controlPoints = controlPoints.slice();
            controlPoints.reverse();
        }
        return controlPoints;
    }
    /**
     * Returns the fold angle of this edge (in degrees, between 0 and 180), or null.
     * @returns {number | null} The fold angle of this edge.
     */
    get foldAngle() {
        return this.edge.foldAngle;
    }
    /**
     * Returns an array containing all the points defining this edge, including the first vertex
     * position, the curve control points, and the second vertex position.
     * @returns {Bezier2} An array of all points defining this edge.
     */
    get allPoints() {
        const { allPoints } = this.edge;
        if (this.isReversed) {
            allPoints.reverse();
        }
        return allPoints;
    }
    /**
     * Returns the vertex opposite the specified vertex in this edge.
     * @param {CreaseVertex} v One of the vertices of this edge.
     * @returns {CreaseVertex} The vertex opposite the specified vertex in this edge.
     */
    getOtherVertex(v) {
        return this.edge.getOtherVertex(v);
    }
    /**
     * Reverses the direction of this edge reference. Used internally.
     */
    _reverse() {
        this._isReversed = !this._isReversed;
    }
    /**
     * Replaces the edge associated with this edge reference. Used internally.
     * @param newEdge The new edge to use.
     */
    _replaceEdge(newEdge) {
        this._edge = newEdge;
    }
    /**
     * Returns an object that can be serialized using `JSON.stringify`.
     * @returns {CreaseJSONEdgeRef} The serializable representation of this edge reference.
     */
    toJSON() {
        const json = { edge: this.edge.index };
        if (this.isReversed) {
            json.isReversed = true;
        }
        return json;
    }
    /**
     * Removes any references / properties on this object.
     */
    destroy() {
        delete this._edge;
        delete this._isReversed;
    }
}
exports.CreaseEdgeRef = CreaseEdgeRef;
//# sourceMappingURL=CreaseEdgeRef.js.map